import { extractList } from './tournamentsApi';

export const DUPLICATE_GROUPS_PAGE_SIZE = 50;

export const DUPLICATE_FIELD_ORDER = ['name', 'family', 'father_name', 'birthday'];

export const REQUIRED_DUPLICATE_FIELDS = ['name', 'family'];

export const DEFAULT_DUPLICATE_FIELDS = [...DUPLICATE_FIELD_ORDER];

export const DUPLICATE_FIELD_OPTIONS = [
  { id: 'family', labelKey: 'dupFieldFamily', locked: true },
  { id: 'name', labelKey: 'dupFieldName', locked: true },
  { id: 'father_name', labelKey: 'dupFieldFather' },
  { id: 'birthday', labelKey: 'dupFieldBirthday' },
];

export const orderedDuplicateFields = (fields = DEFAULT_DUPLICATE_FIELDS) => {
  const selected = new Set((Array.isArray(fields) ? fields : []).map(String));
  REQUIRED_DUPLICATE_FIELDS.forEach((id) => selected.add(id));
  return DUPLICATE_FIELD_ORDER.filter((id) => selected.has(id));
};

export const toggleDuplicateField = (fields, fieldId) => {
  if (REQUIRED_DUPLICATE_FIELDS.includes(fieldId)) {
    return orderedDuplicateFields(fields);
  }
  const current = orderedDuplicateFields(fields);
  if (current.includes(fieldId)) {
    return current.filter((id) => id !== fieldId);
  }
  return orderedDuplicateFields(current.concat(fieldId));
};

const toPlayerId = (player) => {
  const id = Number(player?.id ?? player?.player_id);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const toChampId = (value) => {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

export const normalizeDuplicatePlayer = (player = {}) => {
  const id = toPlayerId(player);
  return {
    ...player,
    id,
    championat_id: toChampId(player.championat_id ?? player.champ_id),
    championat_name: player.championat_name || player.championship_name || player.champ_name || '',
    teams: extractList(player.teams),
  };
};

export const extractDuplicateGroups = (data) => {
  const payload = data && typeof data === 'object'
    ? (Array.isArray(data.groups) ? data : (data.data && typeof data.data === 'object' ? data.data : data))
    : {};
  const rawGroups = Array.isArray(payload.groups) ? payload.groups : [];
  const groups = rawGroups
    .map((group) => (Array.isArray(group) ? group : [])
      .map(normalizeDuplicatePlayer)
      .filter((player) => player.id))
    .filter((group) => group.length >= 2);

  const totalGroups = Number(payload.total_groups);
  const totalPlayers = Number(payload.total_players);
  const pagePlayers = Number(payload.page_players);
  const limitRaw = payload.limit;
  const offsetRaw = payload.offset;
  const limit = limitRaw == null || limitRaw === ''
    ? null
    : (Number.isFinite(Number(limitRaw)) ? Number(limitRaw) : null);
  const offset = Number.isFinite(Number(offsetRaw)) ? Number(offsetRaw) : 0;
  const filterChampId = toChampId(payload.championat_id ?? payload.champ_id);

  return {
    fields: orderedDuplicateFields(payload.fields),
    championat_id: filterChampId,
    limit,
    offset,
    total_groups: Number.isFinite(totalGroups) ? totalGroups : groups.length,
    total_players: Number.isFinite(totalPlayers)
      ? totalPlayers
      : groups.reduce((sum, group) => sum + group.length, 0),
    page_players: Number.isFinite(pagePlayers)
      ? pagePlayers
      : groups.reduce((sum, group) => sum + group.length, 0),
    groups,
  };
};

export const removePlayersFromDuplicateGroups = (groups, playerIds) => {
  const deleted = new Set(
    (Array.isArray(playerIds) ? playerIds : [])
      .map(Number)
      .filter((id) => Number.isFinite(id) && id > 0)
  );
  if (!deleted.size) return Array.isArray(groups) ? groups : [];
  return (Array.isArray(groups) ? groups : [])
    .map((group) => group.filter((player) => !deleted.has(Number(player.id))))
    .filter((group) => group.length >= 2);
};

export const formatDuplicateBirthday = (value, locale) => {
  if (!value) return '';
  const raw = String(value).slice(0, 10);
  const date = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU');
};

export const emptyDuplicateDraft = () => ({ selectedIds: [], keepId: null });

export const canMergeDuplicateDraft = (draft) => {
  const keepId = Number(draft?.keepId);
  const selected = Array.isArray(draft?.selectedIds) ? draft.selectedIds : [];
  if (!Number.isFinite(keepId) || keepId <= 0) return false;
  return selected.some((id) => Number(id) !== keepId);
};

export const mergeDuplicatePayload = (draft) => {
  const keepId = Number(draft?.keepId);
  const duplicateIds = (Array.isArray(draft?.selectedIds) ? draft.selectedIds : [])
    .map(Number)
    .filter((id) => Number.isFinite(id) && id > 0 && id !== keepId);
  return {
    keep_player_id: keepId,
    duplicate_ids: duplicateIds,
  };
};
